import React, { useEffect, useState } from 'react';

// custom components //
import { CustomSpinner } from "_atoms";

// tremor
import { Flex, Subtitle } from "@tremor/react";

// bootstrap
import Col from 'react-bootstrap/Col';
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/esm/Button";
import Row from 'react-bootstrap/Row';


// redux
import { useSelector, useDispatch } from 'react-redux';
import {
    createTask,
    selectCreateTaskStatus,
    clearCreateTaskStatus,
    selectUpdateTaskStatus,
    updateTask,
    resetUpdateTaskFlags,
    saveEmployeeShift
} from "_redux/slices/timeline/timeline-data";
import { selectSelectedTimeline } from "_redux/slices/timeline/timeline-areas";
import { selectBreakTaskEmployee, selectSelectedBreakTask, resetBreakTaskFlags } from '_redux/slices/timeline/selected-task';
import { selectTimelineDate } from '_redux/slices/timeline/timeline-control';
import { selectUsedTheme } from '_redux/slices/timeline/theme';

// styles 
import { Spacing } from '_styles'

// date 
import moment from 'moment';
// functions 

import { convertShiftTimeStamp, convertTimeStampToDate } from "_utils/functions";
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';

// constants 
const breakTaskPeriods = ["15 min", "20 min", "30 min", "45 min", "1 hr"]

const CreateBreakTask = (props) => {
    //#region redux
    const dispatch = useDispatch();
    const selectedTimeline = useSelector(selectSelectedTimeline);
    const createTaskStatus = useSelector(selectCreateTaskStatus);
    const breakTaskEmployee = useSelector(selectBreakTaskEmployee);                       // selected employee to add break task
    const timelineDate = useSelector(selectTimelineDate);                                 // select timeline Date
    const selectedBreakTask = useSelector(selectSelectedBreakTask);                       // select break task to edit 
    const updateTaskStatus = useSelector(selectUpdateTaskStatus);                         // update task status 
    const usedTheme = useSelector(selectUsedTheme);
    const dbUser = useSelector(selectFirebaseInfo);                                       // logged in user

    //#endregion

    //#region local states
    const [breakTaskPeriod, setBreakTaskPeriod] = useState("")                               // holds value of the break Task Period
    const [hourlyIntervals, setHourlyIntervals] = useState([])                                // ordered list of allowable HH:mm values between shift start/end (15 min increments)
    const [assignedTime, setAssignedTime] = useState(null)                                    // legacy timeline field (hourly granularity)
    const [shiftStart, setShiftStart] = useState("")                                // holds value of employee shift start time
    const [shiftEnd, setShiftEnd] = useState("")                                    // holds value of employee shift end time
    const [breakStartTime, setBreakStartTime] = useState("")                        // currently selected break start (HH:mm)
    const [breakEndTime, setBreakEndTime] = useState("")                            // currently selected break end (HH:mm)
    //#endregion

    //#region useEffect

    // listener to set start and end shift time 
    useEffect(() => {
        if (breakTaskEmployee?.shift) {
            const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric' }
            let tempStartsShiftDate = convertTimeStampToDate(breakTaskEmployee.shift?.start, options)
            let startShiftYear = tempStartsShiftDate?.getFullYear();
            let startShiftDay = tempStartsShiftDate?.getDay();
            let currentYear = new Date(timelineDate).getFullYear();
            let currentDay = new Date(timelineDate).getDay();
            // check if employee shift is in current day 
            if (startShiftYear == currentYear && startShiftDay == currentDay) {
                setShiftStart(convertShiftTimeStamp(breakTaskEmployee.shift?.start))
                setShiftEnd(convertShiftTimeStamp(breakTaskEmployee.shift?.end))
                getHourlyIntervals(convertShiftTimeStamp(breakTaskEmployee.shift?.start), convertShiftTimeStamp(breakTaskEmployee.shift?.end))

            }
        }
    }, [breakTaskEmployee?.shift, timelineDate])

    useEffect(() => {
        if (createTaskStatus == "succeeded" || updateTaskStatus == "succeeded") {
            dispatch(resetBreakTaskFlags())
            dispatch(clearCreateTaskStatus());
            dispatch(resetUpdateTaskFlags());
            props.onClose()
        }
    }, [createTaskStatus, updateTaskStatus]);

    // check if there is a selected break task to edit 
    useEffect(() => {
        if (!Object.isObjectEmpty(selectedBreakTask)) {
            const existingPeriod = selectedBreakTask?.breakTaskInformation?.breakTaskPeriod;
            const existingStart = selectedBreakTask?.assignedTime;

            setBreakTaskPeriod(existingPeriod || "");
            setAssignedTime(existingStart || null);
            setBreakStartTime(existingStart || "");

            if (existingStart && existingPeriod) {
                const duration = parsePeriodToMinutes(existingPeriod);
                if (duration) {
                    const endTime = moment(existingStart, "HH:mm").add(duration, "minutes").format("HH:mm");
                    setBreakEndTime(endTime);
                } else {
                    setBreakEndTime("");
                }
            } else {
                setBreakEndTime("");
            }
        } else {
            setBreakTaskPeriod("");
            setAssignedTime(null);
            setBreakStartTime("");
            setBreakEndTime("");
        }
    }, [selectedBreakTask])

    useEffect(() => {
        if (breakStartTime && breakEndTime) {
            const duration = moment(breakEndTime, "HH:mm").diff(moment(breakStartTime, "HH:mm"), "minutes");
            if (duration > 0) {
                setBreakTaskPeriod(formatMinutesToPeriod(duration));
            } else {
                setBreakTaskPeriod("");
            }
        } else {
            setBreakTaskPeriod("");
        }
    }, [breakStartTime, breakEndTime])
    //#endregion

    //#region functions

    // get times between shift start and end to be assign time choices 
    const getHourlyIntervals = (start, end) => {
        // Build the selectable time list (HH:mm) between the shift bounds inclusive.
        if (!start || !end) {
            setHourlyIntervals([]);
            return;
        }

        const startMoment = moment(start, "HH:mm");
        const endMoment = moment(end, "HH:mm");

        if (!startMoment.isValid() || !endMoment.isValid() || endMoment.isSameOrBefore(startMoment)) {
            setHourlyIntervals([]);
            return;
        }

        const intervals = [];
        const current = startMoment.clone();

        while (current.isSameOrBefore(endMoment)) {
            intervals.push(current.format("HH:mm"));
            current.add(15, "minutes");
        }

        setHourlyIntervals(intervals);
    }

    const parsePeriodToMinutes = (period) => {
        // Accept strings like "1 hr 45 min" or "30 min" and convert to minutes.
        if (!period) return 0;
        if (period.includes("hr")) {
            return parseInt(period, 10) * 60;
        }
        return parseInt(period, 10);
    };

    const formatMinutesToPeriod = (minutes) => {
        if (!minutes) return "";

        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        const parts = [];

        if (hours > 0) {
            parts.push(`${hours} ${hours === 1 ? "hr" : "hrs"}`);
        }

        if (remainingMinutes > 0) {
            parts.push(`${remainingMinutes} min`);
        }

        return parts.join(" ");
    };

    const clampTime = (value, min, max) => {
        // Keep times inside the shift boundaries (returns HH:mm).
        const timeMoment = moment(value, "HH:mm");
        if (!timeMoment.isValid()) return value;

        if (min) {
            const minMoment = moment(min, "HH:mm");
            if (minMoment.isValid() && timeMoment.isBefore(minMoment)) {
                return minMoment.format("HH:mm");
            }
        }

        if (max) {
            const maxMoment = moment(max, "HH:mm");
            if (maxMoment.isValid() && timeMoment.isAfter(maxMoment)) {
                return maxMoment.format("HH:mm");
            }
        }

        return timeMoment.format("HH:mm");
    };

    const handleBreakStartChange = (value) => {
        // Snap the start within bounds and auto-adjust the end if it slips before the start.
        let normalized = clampTime(value, startMinTime, startMaxTime);

        setBreakStartTime(normalized);
        setAssignedTime(normalized);

        if (breakEndTime) {
            const startMoment = moment(normalized, "HH:mm");
            const endMoment = moment(breakEndTime, "HH:mm");
            if (!endMoment.isValid() || !startMoment.isValid() || endMoment.isSameOrBefore(startMoment)) {
                const proposedEnd = startMoment.clone().add(15, "minutes");
                const clampedEnd = clampTime(proposedEnd.format("HH:mm"), normalized, endMaxTime);
                if (moment(clampedEnd, "HH:mm").isAfter(startMoment)) {
                    setBreakEndTime(clampedEnd);
                } else {
                    setBreakEndTime("");
                }
            } else {
                const clampedEnd = clampTime(breakEndTime, normalized, endMaxTime);
                setBreakEndTime(clampedEnd);
            }
        }
    };

    const handleBreakEndChange = (value) => {
        // Ensure the end always stays after the start (minimum 15 min).
        if (!breakStartTime) {
            setBreakEndTime("");
            return;
        }

        let normalized = clampTime(value, breakStartTime, endMaxTime);

        const startMoment = moment(breakStartTime, "HH:mm");
        const endMoment = moment(normalized, "HH:mm");

        if (!startMoment.isValid() || !endMoment.isValid()) {
            setBreakEndTime("");
            return;
        }

        if (endMoment.isSameOrBefore(startMoment)) {
            const proposedEnd = startMoment.clone().add(15, "minutes");
            const clampedEnd = clampTime(proposedEnd.format("HH:mm"), breakStartTime, endMaxTime);
            if (moment(clampedEnd, "HH:mm").isAfter(startMoment)) {
                setBreakEndTime(clampedEnd);
            } else {
                setBreakEndTime("");
            }
            return;
        }

        setBreakEndTime(normalized);
    };

    const generateShiftFromAssignedTime = (assignedTime, period) => {
        const durationMinutes = parsePeriodToMinutes(period);

        if (!assignedTime || !durationMinutes) {
            return null;
        }

        // Today’s date
        const today = moment().format("YYYY-MM-DD");

        // Start datetime
        const startShift = moment(`${today}T${assignedTime}`);

        if (!startShift.isValid()) {
            return null;
        }

        // End datetime
        const endShift = moment(startShift).add(durationMinutes, "minutes");

        if (!endShift.isValid()) {
            return null;
        }

        return {
            start: startShift.toISOString(),
            end: endShift.toISOString()
        };
    };

    //TODO, add break task into shift docs.
    // create break task
    const createBreakTask = () => {
        // Guard against partially completed forms so we never dispatch malformed payloads.
        if (!assignedTime || !breakStartTime || !breakEndTime || !breakTaskPeriod) {
            return;
        }

        // Convert HH:mm into a Date anchored to today. This yields valid timestamps for the backend.
        const createDateFromTime = (timeString, baseDate = new Date()) => {
            const [hours, minutes] = timeString.split(':').map(Number);
            const date = new Date(baseDate);
            date.setHours(hours, minutes, 0, 0);
            return date;
        }

        let taskObject = {
            active: false,
            completed: false,
            employeeId: breakTaskEmployee?.employeeId,
            assignedTime: `${assignedTime?.split(":")?.[0]}:00`,
            flightInformation: null,
            passengerInformation: null,
            flightBooking: false,
            allocatedTime: new Date(),
            _id: `${breakTaskEmployee?.employeeId}-breakTask-${new Date().getHours()}-${new Date().getMinutes()}-${new Date().getSeconds()}`,
            cancelled: false,
            pending: false,
            selectedTimeline: selectedTimeline?._id ?? null,
            dropOffLocation: null,
            isTransitBooking: false,
            notPlanned: false,
            isEstimatedBooking: false,
            passengerDutTime: null,
            combinedBooking: false,
            passengers: null,
            isBreakTask: true,
            breakTaskInformation: {
                breakTaskPeriod: breakTaskPeriod,
                breakTaskDate: moment(new Date()).format().split("T")[0],
                employeeName: breakTaskEmployee?.title,
                airport: breakTaskEmployee?.airport,
                // Provide both full ISO timestamps and HH:mm strings for downstream consumers/reporting.
                breakStartTime: createDateFromTime(breakStartTime),
                breakEndTime: (() => {
                    const start = createDateFromTime(breakStartTime);
                    const end = createDateFromTime(breakEndTime);
                    // If end time is before start time, the break crosses midnight — add a day
                    if (end <= start) {
                        end.setDate(end.getDate() + 1);
                    }
                    return end;
                })(),
            }
        };
        if (dbUser.details.authority == "DUB") {
            // DUB controllers expect new break tasks to transition directly into the active column.
            taskObject = {
                ...taskObject,
                active: true,
                startTime: new Date(),
            }
        }
        dispatch(createTask(taskObject));
        saveBreak()
    }

    // update break task 
    const editBreakTask = () => {
        let taskUpdates = {
            id: selectedBreakTask._id,
            assignedTime: assignedTime,
            breakTaskInformation: {
                ...selectedBreakTask?.breakTaskInformation,
                breakTaskPeriod: breakTaskPeriod,
            }

        }
        dispatch(updateTask(taskUpdates))
        saveBreak()
    }
    //This function will add break task into staff shift
    const saveBreak = () => {
        const shift = generateShiftFromAssignedTime(assignedTime, breakTaskPeriod);
        if (!shift || !breakTaskEmployee) {
            return;
        }
        // Persist the break as part of the employee shift document so the schedule reflects the new slot.
        const breakPayload = {
            userId: breakTaskEmployee.userId,
            airport: breakTaskEmployee.airport,
            employeeId: breakTaskEmployee.employeeId,
            shift,
            isBreakTask: true
        }
        dispatch(saveEmployeeShift(breakPayload))
    }

    //#endregion


    const startMinTime = hourlyIntervals.length ? hourlyIntervals[0] : shiftStart;
    const startMaxTime = hourlyIntervals.length ? hourlyIntervals[hourlyIntervals.length - 1] : shiftEnd;
    const endMinTime = breakStartTime || startMinTime;
    const endMaxTime = startMaxTime;
    return (
        <div style={{ padding: Spacing.SCALE_12, zIndex: "initial" }}>
            <div style={{ display: "flex" }}>
                <div style={{ marginLeft: Spacing.SCALE_12, marginBottom: Spacing.SCALE_22, width: "100%" }}>
                    <Col>
                        <div style={{ marginTop: Spacing.SCALE_22 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", }}>
                                <div style={{}}>
                                    <Subtitle>Employee Name</Subtitle>
                                    <p>{breakTaskEmployee ? breakTaskEmployee?.title : ""}</p>
                                </div>
                                <div >
                                    <Subtitle>Employee ID</Subtitle>
                                    <p>{breakTaskEmployee ? breakTaskEmployee?.employeeId : ""}</p>
                                </div>
                                <div >
                                    <Subtitle>Shift Start</Subtitle>
                                    <p>{shiftStart ? shiftStart : ""}</p>
                                </div>
                                <div >
                                    <Subtitle>Shift End</Subtitle>
                                    <p>{shiftEnd ? shiftEnd : ""}</p>
                                </div>
                            </div>
                        </div>
                        <div style={{ marginTop: Spacing.SCALE_22 }}>
                            <Row>
                                <Col xs={12} md={6}>
                                    <Subtitle>Break Start Time</Subtitle>
                                    <Form.Control
                                        type="time"
                                        value={breakStartTime}
                                        min={startMinTime || undefined}
                                        max={startMaxTime || undefined}
                                        disabled={!hourlyIntervals.length}
                                        onChange={(event) => handleBreakStartChange(event.target.value)}
                                    />
                                </Col>
                                <Col xs={12} md={6}>
                                    <Subtitle>Break End Time</Subtitle>

                                    <Form.Control
                                        type="time"
                                        value={breakEndTime}
                                        min={endMinTime || undefined}
                                        max={endMaxTime || undefined}
                                        // step={900}
                                        disabled={!breakStartTime}
                                        onChange={(event) => handleBreakEndChange(event.target.value)}
                                    />
                                </Col>
                                <p style={{ fontSize: 16, color: "red", marginTop: Spacing.SCALE_22 }}>Please note that the break time should be within the staff shift time</p>
                            </Row>
                        </div>

                        <div style={{ marginTop: Spacing.SCALE_22 }}>
                            <Flex justifyContent="between" alignItems="center">
                                <Subtitle>Break Duration</Subtitle>
                                <p style={{ margin: 0 }}>{breakTaskPeriod || "--"}</p>
                            </Flex>
                        </div>

                        <div style={{ marginTop: Spacing.SCALE_22 }}>
                            {/* check if selected break task Edit Btn or create */}
                            {!Object.isObjectEmpty(selectedBreakTask) ?
                                <Button variant="outline-primary" onClick={() => editBreakTask()} disabled={!assignedTime || !breakTaskPeriod || !breakTaskEmployee}>
                                    {updateTaskStatus == "loading" ? <CustomSpinner /> : "Edit Task"}
                                </Button>
                                :
                                <Button variant="outline-primary" onClick={createBreakTask} disabled={!assignedTime || !breakTaskPeriod || !breakTaskEmployee}>
                                    {createTaskStatus == "loading" ? <CustomSpinner /> : "Confirm Assignment"}
                                </Button>
                            }
                        </div>
                    </Col>
                </div>
            </div>
        </div>
    )
}

export default CreateBreakTask
